import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ALL_PROFILES_QUERY_KEY } from "@/lib/users-cache";
import { getUnknownErrorMessage } from "@/lib/error-message";
import { userService } from "@/services/UserService";
import type { Profile } from "@/types/users";

type DeleteUserMutationContext = {
  previousProfiles: Profile[] | undefined;
};

export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => userService.delete(userId),
    onMutate: async (userId): Promise<DeleteUserMutationContext> => {
      await queryClient.cancelQueries({ queryKey: ALL_PROFILES_QUERY_KEY });

      const previousProfiles = queryClient.getQueryData<Profile[]>(
        ALL_PROFILES_QUERY_KEY,
      );

      queryClient.setQueryData<Profile[]>(ALL_PROFILES_QUERY_KEY, (old) =>
        (old ?? []).filter((user) => user.id !== userId),
      );

      return { previousProfiles };
    },
    onSuccess: () => {
      toast.success("User deleted successfully!");
    },
    onError: (error: unknown, _userId, context) => {
      if (context?.previousProfiles !== undefined) {
        queryClient.setQueryData(
          ALL_PROFILES_QUERY_KEY,
          context.previousProfiles,
        );
      }
      toast.error(getUnknownErrorMessage(error));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ALL_PROFILES_QUERY_KEY });
    },
  });
};
